import React, { useContext, useEffect, useState } from "react";
import "./Results.css";

import { Link } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { makeGetAPICall } from "../../helpers/ApiHelpers";
import Swal from "sweetalert2";
import { MathJax } from "better-react-mathjax";
import sanitizeHtml from "sanitize-html";
import { getFromStorage, hasContent, insertNewLine, sanitizeHtmlOption } from "../../assets";

function Results() {
	const { exam, setExam, user } = useContext(AppContext);

	const [selections, setSelections] = useState(null);

	const result = JSON.parse(getFromStorage("result"));


	const endExam = () => {
		localStorage.removeItem("result");
		localStorage.removeItem("exam");
		localStorage.removeItem("currentQuestion");
		localStorage.removeItem("answers");
		localStorage.removeItem("timeLeft");
		localStorage.removeItem("tsc");
		localStorage.removeItem("tab_switch_count");
		setExam(null);
	};

	useEffect(() => {
		//? Only fetch the student's selections if show_summary is explicitly "1".
		//? Checking === "1" (not just truthy) prevents this from firing when
		//? show_summary is "0" — a non-empty string is truthy in JS, so a plain
		//? if (exam?.show_summary) would incorrectly fire for "0" too.
		if (String(exam?.show_summary) === "1") {
			makeGetAPICall(
				"exams.php",
				{
					action: "list_student_selections",
					exam_id: exam?.id,
					student_id: user.id,
				},
				(response) => {
					setSelections(response.data);
				},
				() => {
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "Couldn't get your answers for this exam. Please inform a teacher, refresh and try again",
					});
				}
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//? Split selections into objective and theory, then recombine so objective
	//? questions always come first in the review list. ?? [] ensures we never
	//? spread undefined if the filter returns nothing (e.g. no theory questions).
	const objectiveQuestions = selections?.selection?.filter((selection) => selection?.question?.type === "objective");
	const theoryQuestions = selections?.selection?.filter((selection) => selection?.question?.type === "theory");
	const questions = [...(objectiveQuestions ?? []), ...(theoryQuestions ?? [])];

	return (
		<>
			<div className="results-container">
				<div className="header-text">EXAM COMPLETED</div>

				{hasContent(exam?.end_instructions) && (
					<div className="mt-3">
						<div className="row">
							<div className="row">
								<div className="col-lg-2">
									<p className="header-content-text">Instruction:</p>
								</div>
								<div
									className="col-lg-10 instruction"
									dangerouslySetInnerHTML={{
										__html: exam?.end_instructions,
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{/*? Show the exam result summary only if show_result === "1".
			    Checking strict string equality because the API returns "1"/"0"
			    as strings — loose truthiness would incorrectly show results
			    when show_result is "0" since "0" is a truthy non-empty string. */}
			{String(exam?.show_result) === "1" && (
				<div className="result-text">
					<div className="col-12 d-flex justify-content-center align-items-center mt-4 summary-row bg-secondary" style={{ color: "var(-primary-color)" }}>
						EXAM SUMMARY
					</div>

					<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
						<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
							<p className="m-0">Total Number of Questions</p>
							<p className="m-0">{exam?.total_question}</p>
						</div>
					</div>

					<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
						<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
							<p className="m-0">Number of Questions Attempted</p>
							<p className="m-0">{result.q_attempted}</p>
						</div>
					</div>

					<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
						<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
							<p className="m-0">Number of Correct Answers</p>
							<p className="m-0">{result.q_correct}</p>
						</div>
					</div>

					<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
						<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
							<p className="m-0">Number of Wrong Answers</p>
							<p className="m-0">{result.q_failed}</p>
						</div>
					</div>

					{parseInt(exam?.switch_tab_limit || 0) > 0 && (
						<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
							<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
								<p className="m-0">Tab Switches</p>
								<p className="m-0">{result.tab_switch_count}</p>
							</div>
						</div>
					)}

					{/*? Score display — two different layouts depending on whether a
					    malpractice penalty was applied:
					    	- If penalty_applied > 0: show raw_score (initial), penalty deduction,
					    	  and final score separately so the student can see the breakdown.
					    	- If no penalty: just show Score once, cleanly.
					    Previously Score appeared twice when penalty_applied was 0 because
					    it was rendered both before this conditional AND inside the else block.
					    Removed the pre-conditional Score row and let this block handle both cases. */}
					{result.penalty_applied > 0 ? (
						<>
							<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
								<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
									<p className="m-0">Initial Score</p>
									<p className="m-0">{result.raw_score?.toFixed(0)} %</p>
								</div>
							</div>
							<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
								<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
									<p className="m-0">Penalty Applied</p>
									<p className="m-0" style={{ color: "red" }}>
										-{result.penalty_applied?.toFixed(0)} %
									</p>
								</div>
							</div>
							<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
								<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
									<p className="m-0" style={{ fontWeight: "bold" }}>Final Score</p>
									<p className="m-0" style={{ fontWeight: "bold" }}>{result.score.toFixed(0)} %</p>
								</div>
							</div>
						</>
					) : (
						<div className="col-12 d-flex justify-content-center align-items-center summary-row bg-white">
							<div className="col-8 d-flex justify-content-between align-items-center" style={{ maxWidth: 400 }}>
								<p className="m-0">Score</p>
								<p className="m-0">{result.score.toFixed(0)} %</p>
							</div>
						</div>
					)}
				</div>
			)}

			{/*? Question review section — only rendered if:
			    	1. show_summary === "1" (strict string check, same reason as show_result)
			    	2. questions array is non-empty (selections fetched and processed successfully)
			    If either condition fails the whole review table is hidden. */}

			{String(exam?.show_summary) === "1" && !!questions?.length && (
				<div className="table-responsive">
					{questions?.map((selection, selectionIndex) => {
						let question = selection.question;
						return (
							<div className="qa-container" key={`question-${selectionIndex}`}>
								<div className="qa-number">
									{question?.type === "theory" && <span className="theory-tag">Theory</span>}
									Question {selectionIndex + 1}

									{question?.type === "objective" && (
										<div className="d-flex">
											{selection.marks !== "0" ? (
												<div className="qa-correct">Correct</div>
											) : (
												<div className="qa-incorrect">Incorrect</div>
											)}
										</div>
									)}
								</div>
								<MathJax>
									<div
										className="qa-text"
										dangerouslySetInnerHTML={{
											__html: sanitizeHtml(insertNewLine(question.question_title), sanitizeHtmlOption),
										}}
									/>
								</MathJax>
								{question?.type === "objective" && (
									<div className="qa-option-container">
										<div>
											<div className="row">
												{Object.keys(question.options).map((optionKey, index) => {
													let option = question.options[optionKey];
													return (
														<div className="col-md-6" key={`option-${optionKey}`}>
															<MathJax>
																<div
																	className="option"
																	dangerouslySetInnerHTML={{
																		__html: `<div class="d-flex align-items-center mb-2"><p class="option-label">Option ${index + 1}</p>${selection.answer_option === optionKey
																			? '<p class="qa-selected">Selected</p>'
																			: ""
																			}</div>${sanitizeHtml(insertNewLine(option), sanitizeHtmlOption)}`,
																	}}
																/>
															</MathJax>
														</div>
													);
												})}
											</div>
											<p className="option-label correct m-0 p-0">
												Correct Answer: Option {question.answer_option}
											</p>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<Link to="/home" onClick={() => endExam()}>
				<button className="start">End Exam</button>
			</Link>
		</>
	);
}

export default Results;